/**
 * Tenant Configuration Store
 *
 * Centralized store for tenant-specific settings that need to be
 * synchronized between backend and frontend for consistent formatting.
 *
 * This store is loaded once on app initialization and cached.
 * Settings are refreshed when:
 * - User logs in
 * - User switches tenant
 * - Settings are updated in the settings page
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/api';

// Types for tenant configuration
export interface DateConfig {
  format: string;      // PHP format: 'd.m.Y', 'Y-m-d', etc.
  timezone: string;    // e.g., 'Europe/Bratislava'
}

export interface TimeConfig {
  format: string;      // PHP format: 'H:i', 'H:i:s', 'h:i A'
}

export interface NameConfig {
  format: number;      // 1 = LastName FirstName, 2 = FirstName LastName
}

export interface CurrencyConfig {
  default: string;     // e.g., 'EUR', 'CZK', 'USD'
  locale: string;      // e.g., 'sk-SK', 'cs-CZ', 'en-GB'
}

export interface LocaleNames {
  dayNames: {
    full: string[];    // ['Nedeľa', 'Pondelok', ...]
    short: string[];   // ['Ne', 'Po', ...]
  };
  monthNames: string[]; // ['január', 'február', ...]
}

export interface TenantConfig {
  date: DateConfig;
  time: TimeConfig;
  locale: string;      // 'sk', 'cs', 'en'
  name: NameConfig;
  currency: CurrencyConfig;
  dayNames: LocaleNames['dayNames'];
  monthNames: string[];
}

interface ConfigState {
  // Configuration data
  config: TenantConfig | null;
  
  // Loading state
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  
  // Last fetch timestamp
  lastFetched: number | null;
  
  // Actions
  setConfig: (config: TenantConfig) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchConfig: (forceRefresh?: boolean) => Promise<void>;
  clearConfig: () => void;
  
  // Getters for common values
  getDateFormat: () => string;
  getTimeFormat: () => string;
  getTimezone: () => string;
  getLocale: () => string;
  /** Tenant default language from DB (settings.Language). Used as required language for translatable fields (news, pages, positions, org units). */
  getDefaultRequiredLanguage: () => 'sk' | 'cs' | 'en';
  getNameFormat: () => number;
  getCurrency: () => string;
  getCurrencyLocale: () => string;
}

// Default configuration (Slovak defaults)
const defaultConfig: TenantConfig = {
  date: {
    format: 'd.m.Y',
    timezone: 'Europe/Bratislava',
  },
  time: {
    format: 'H:i',
  },
  locale: 'sk',
  name: {
    format: 1, // LastName FirstName
  },
  currency: {
    default: 'EUR',
    locale: 'sk-SK',
  },
  dayNames: {
    full: ['Nedeľa', 'Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota'],
    short: ['Ne', 'Po', 'Ut', 'St', 'Št', 'Pi', 'So'],
  },
  monthNames: ['január', 'február', 'marec', 'apríl', 'máj', 'jún', 'júl', 'august', 'september', 'október', 'november', 'december'],
};

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      config: null,
      isLoading: false,
      isLoaded: false,
      error: null,
      lastFetched: null,

      setConfig: (config) => set({ 
        config, 
        isLoaded: true, 
        error: null,
        lastFetched: Date.now(),
      }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      fetchConfig: async (forceRefresh = false) => {
        const state = get();
        
        // Check if we have a valid cached config (skip if forceRefresh)
        if (!forceRefresh && state.config && state.lastFetched) {
          const age = Date.now() - state.lastFetched;
          if (age < CACHE_DURATION) {
            return; // Use cached config
          }
        }

        set({ isLoading: true, error: null });

        try {
          const response = await api.get<{ success: boolean; data: TenantConfig; error?: string }>('/api/settings/formatting');

          if (response.data.success && response.data.data) {
            set({
              config: response.data.data,
              isLoading: false,
              isLoaded: true,
              error: null,
              lastFetched: Date.now(),
            });
          } else {
            throw new Error(response.data.error || 'Invalid response format');
          }
        } catch (error) {
          console.error('Failed to fetch tenant config:', error);
          
          // Use default config on error
          set({
            config: defaultConfig,
            isLoading: false,
            isLoaded: true,
            error: error instanceof Error ? error.message : 'Unknown error',
            lastFetched: Date.now(),
          });
        }
      },

      clearConfig: () => set({
        config: null,
        isLoading: false,
        isLoaded: false,
        error: null,
        lastFetched: null,
      }),

      // Getters with fallback to defaults
      getDateFormat: () => get().config?.date.format ?? defaultConfig.date.format,
      getTimeFormat: () => get().config?.time.format ?? defaultConfig.time.format,
      getTimezone: () => get().config?.date.timezone ?? defaultConfig.date.timezone,
      getLocale: () => get().config?.locale ?? defaultConfig.locale,
      getDefaultRequiredLanguage: () => {
        const locale = get().config?.locale ?? defaultConfig.locale;
        return (locale === 'cs' || locale === 'en' ? locale : 'sk') as 'sk' | 'cs' | 'en';
      },
      getNameFormat: () => get().config?.name.format ?? defaultConfig.name.format,
      getCurrency: () => get().config?.currency.default ?? defaultConfig.currency.default,
      getCurrencyLocale: () => get().config?.currency.locale ?? defaultConfig.currency.locale,
    }),
    {
      name: 'tenant-config',
      partialize: (state) => ({
        config: state.config,
        lastFetched: state.lastFetched,
        isLoaded: state.isLoaded,
      }),
    }
  )
);

// Export default config for use in utilities
export { defaultConfig };

// Helper to get config synchronously (for use in formatting functions)
export const getConfig = (): TenantConfig => {
  const state = useConfigStore.getState();
  return state.config ?? defaultConfig;
};

// Helper to ensure config is loaded
export const ensureConfigLoaded = async (): Promise<TenantConfig> => {
  const state = useConfigStore.getState();
  
  if (!state.isLoaded || !state.config) {
    await state.fetchConfig();
  }
  
  return useConfigStore.getState().config ?? defaultConfig;
};
